import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, AlertTriangle, ArrowRight, Rocket, CalendarClock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API, fmtDate } from '../../lib/planConstants';

export default function PlanNextActions() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/futurepath-plan/coach/next-actions`, getAuthHeader ? getAuthHeader() : {})
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return null;

  if (!data.has_plan) {
    return (
      <div className="glass-card p-5" data-testid="plan-cta-card">
        <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold flex items-center gap-1.5">
          <Rocket className="w-3.5 h-3.5" /> Launch
        </p>
        <h3 className="text-base md:text-lg font-semibold text-white mt-1">Turn your journey into a living plan</h3>
        <p className="text-sm text-slate-300 mt-1">
          Pick where your plan should end, graduation, enrollment, licensing, a career, and we'll build the milestones,
          tasks and deadlines to get you there.
        </p>
        <Link to="/app/my-plan" data-testid="plan-cta-link"
          className="btn-primary inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full text-xs font-semibold">
          Launch My Plan <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  const s = data.signals || {};
  return (
    <div className="glass-card p-5" data-testid="plan-next-actions">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> My plan · {data.progress?.percent}% complete
        </p>
        <Link to="/app/my-plan" className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
          data-testid="plan-open-link">Open plan <ArrowRight className="w-3 h-3" /></Link>
      </div>

      {s.next_best_action && (
        <div className="mt-3">
          <p className="text-xs text-slate-300">Next best action</p>
          <p className="text-sm font-semibold text-white mt-0.5">{s.next_best_action.title}</p>
          <p className="text-xs text-slate-300 mt-0.5">Due {fmtDate(s.next_best_action.due_date)}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        {data.progress?.overdue > 0 && (
          <span className="text-[11px] px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {data.progress.overdue} overdue
          </span>
        )}
        {(s.due_soon || []).length > 0 && (
          <span className="text-[11px] px-2 py-1 rounded-full bg-cyan-500/15 text-cyan-300 inline-flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> {s.due_soon.length} due in 14 days
          </span>
        )}
        {data.checkin_due && (
          <Link to="/app/my-plan" className="text-[11px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-300"
            data-testid="plan-checkin-badge">Weekly check-in ready</Link>
        )}
      </div>
    </div>
  );
}
